import { InfoOutlineIcon } from '@chakra-ui/icons';
import type { ComponentWithAs, IconProps } from '@chakra-ui/react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button as ChakraButton,
  Divider,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Select, type SingleValue } from 'chakra-react-select';
import { get } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { type FormState, type RegisterOptions, useForm } from 'react-hook-form';

import { useIsRevelioAdmin } from '@revelio/auth';
import type { ColumnMappings, Subsidiary } from '@revelio/core';
import { WithTooltip } from '@revelio/core';
import { Button } from '@revelio/ui';

import {
  DatabaseRefIcon,
  DiscoveryIcon,
  ListSelectIcon,
  StarIcon,
} from '../../../shared/ui/icons';
import { CompanySelectionsModal } from '../../deliverables/company-selection/subsidiary-mapping/company-selections-modal';
import {
  SnowflakeDbInputs,
  type SnowflakeDbValues,
  type SnowflakeRegister,
} from '../../deliverables/snowflake-db-inputs';
import {
  createCompanyMapperFromMappings,
  createCompanyMapperFromSelections,
  enrichSubsidiaryList,
} from '../api/company-mapper-api';
import type {
  CompanyMapperSelectionType,
  DataBuilderCompanyMapper,
} from '../api/data-builder-api.types';
import { useCompanySelectionGate } from '../CompanySelectionGateContext';
import type {
  DataBuilderCompanySelections,
  DataBuilderCompanySet,
} from '../data-builder.store';
import { formatCompanySetName } from '../utils/selection-summary';
import { DataBuilderMapCompaniesFlow } from './DataBuilderMapCompaniesFlow';
import { DataBuilderSubsidiaryMapping } from './DataBuilderSubsidiaryMapping';
import { SelectionOptionCard } from './ui/SelectionOptionCard';

const PIPELINE_INPUT_FIELDS: {
  name: keyof SnowflakeDbValues;
  label: string;
}[] = [
  { name: 'database', label: 'Database' },
  { name: 'schema', label: 'Schema' },
  { name: 'table', label: 'Table' },
];

type CompanySelectionModalProps = {
  instanceId: string;
  isOpen: boolean;
  onClose: () => void;
  selectedCompanySelections: DataBuilderCompanySelections;
  selectedCompanySets: DataBuilderCompanySet[] | null;
  selectedCompanyMapper?: DataBuilderCompanyMapper | null;
  onApplySelections: (value: DataBuilderCompanySelections) => void;
  onApplyCompanySets: (value: DataBuilderCompanySet[]) => void;
  onApplyMapper: (value: DataBuilderCompanyMapper | null) => void;
};

type CardOption = {
  id: string;
  label: string;
  description: string;
  icon: ComponentWithAs<'svg', IconProps>;
  enabled: boolean;
  adminOnly?: boolean;
};

const DEFAULT_CARD_ID = 'select_companies';
const COMMON_SET_CARD_ID = 'common_set';
const CHOOSE_FROM_LIST_CARD_ID = 'choose_from_list';
const MAP_COMPANIES_CARD_ID = 'map_companies';
const FUTURE_RELEASE_TOOLTIP =
  'This option will be implemented in a future release';

const cardIdForSelectionType = (
  selectionType: CompanyMapperSelectionType | null | undefined
): string | null => {
  if (selectionType === 'table') return CHOOSE_FROM_LIST_CARD_ID;
  if (selectionType === 'selection') return DEFAULT_CARD_ID;
  if (selectionType === 'file') return MAP_COMPANIES_CARD_ID;
  return null;
};

type CompanySetOption = {
  value: DataBuilderCompanySet;
  label: string;
};

const SUPPORTED_COMPANY_SET_VALUES: DataBuilderCompanySet[] = [
  'eurostoxx_50',
  'ftse_100',
  'nasdaq_100',
  'public_us',
  'revelio_shared',
  'revelio_shared_private',
  'revelio_shared_public',
  'russell_1000',
  'sp_500',
];

const SUPPORTED_COMPANY_OPTIONS: CompanySetOption[] =
  SUPPORTED_COMPANY_SET_VALUES.map((value) => ({
    value,
    label: formatCompanySetName(value).toUpperCase(),
  }));

const ONLY_REVELIO_ADMIN_OPTIONS: DataBuilderCompanySet[] = [
  'revelio_shared',
  'revelio_shared_private',
  'revelio_shared_public',
];

const CARD_OPTIONS: CardOption[] = [
  {
    id: 'common_set',
    label: 'Choose a common set',
    description: 'Select companies from favorite lists',
    icon: StarIcon,
    enabled: true,
  },
  {
    id: 'choose_from_list',
    label: 'Upload a manual mapping',
    description: 'Upload an existing company mapping input from a database',
    icon: DatabaseRefIcon,
    enabled: true,
    adminOnly: true,
  },
  {
    id: 'select_companies',
    label: 'Select companies',
    description: 'Add the companies that you want',
    icon: ListSelectIcon,
    enabled: true,
  },
  {
    id: 'map_companies',
    label: 'Map Companies',
    description: 'Map a list of provided companies to our company universe',
    icon: DiscoveryIcon,
    enabled: true,
  },
  {
    id: 'saved_companies',
    label: 'Saved companies lists',
    description: 'Choose from your saved companies',
    icon: StarIcon,
    enabled: false,
  },
];

type SubModalView =
  | 'none'
  | 'common_set_selection'
  | 'entity_selection'
  | 'map_companies'
  | 'ref_table'
  | 'subsidiary_mapping';

type CloseIntent = 'close_all' | 'close_submodal' | null;
type SubsidiaryFlowSource = 'entity_selection' | 'map_companies';

type CompanyRefFormValues = {
  companyReference: SnowflakeDbValues;
  pipelineInput: SnowflakeDbValues;
};

function parseSnowflakePath(
  path: string | null | undefined
): SnowflakeDbValues {
  if (path === undefined || path === null || path === '') {
    return {
      database: '',
      schema: '',
      table: '',
    };
  }

  const segments = path.split('.').filter((value) => value !== '');
  const table = segments[segments.length - 1] ?? '';
  const schema =
    segments.length > 1 ? (segments[segments.length - 2] ?? '') : '';
  const database =
    segments.length > 2 ? segments.slice(0, segments.length - 2).join('.') : '';

  return {
    database,
    schema,
    table,
  };
}

function joinSnowflakePath(values: SnowflakeDbValues): string {
  const parts = [values.database, values.schema, values.table].filter(
    (value) => value !== ''
  );
  return parts.join('.');
}

function normalizeToSubsidiary(sel: Subsidiary): Subsidiary {
  const primaryName = get(sel, 'primary_name');
  const label = get(sel, 'label');
  const rawEmpCount = get(sel, 'raw_emp_count');
  const hasSubsidiariesRaw = get(sel, 'has_subsidiaries');
  const name =
    sel.title ||
    (typeof primaryName === 'string' ? primaryName : '') ||
    (typeof label === 'string' ? label : '') ||
    String(sel.rcid);
  const rawHeadCount =
    typeof rawEmpCount === 'string' || typeof rawEmpCount === 'number'
      ? Number(rawEmpCount)
      : undefined;

  return {
    title: name,
    id: String(sel.rcid),
    company_id: sel.company_id ?? String(sel.rcid),
    hasSubsidiaries:
      typeof hasSubsidiariesRaw === 'boolean'
        ? hasSubsidiariesRaw
        : Boolean(sel.hasSubsidiaries),
    headCount: Number(rawHeadCount ?? sel.headCount ?? 0),
    rcid: Number(sel.rcid),
    isSelected: true,
    isIndeterminate: false,
  };
}

function toCompanySelectionMap(
  selections: Subsidiary[]
): DataBuilderCompanySelections {
  const out: DataBuilderCompanySelections = {};
  for (const selection of selections) {
    const sub = normalizeToSubsidiary(selection);
    const rcid = String(sub.rcid);
    out[rcid] = {
      ...sub,
      primary_name: sub.title,
      label: sub.title,
    };
  }
  return out;
}

function buildInitialMappings(companies: Subsidiary[]): ColumnMappings {
  return {
    primary: {
      title: 'Primary',
      name: 'Primary',
      selectAll: true,
      isLoading: false,
      subsidiaries: [...companies].sort((a, b) => b.headCount - a.headCount),
    },
  };
}

function mappingsToSelectionMap(
  mappings: ColumnMappings
): DataBuilderCompanySelections {
  const primary = mappings.primary;
  if (!primary?.subsidiaries) return {};
  const out: DataBuilderCompanySelections = {};
  for (const sub of primary.subsidiaries.filter((s) => s.isSelected)) {
    const rcid = String(sub.rcid);
    out[rcid] = {
      ...sub,
      primary_name: sub.title,
      label: sub.title,
    };
  }
  return out;
}

export const CompanySelectionModal = ({
  instanceId,
  isOpen,
  onClose,
  selectedCompanySelections,
  selectedCompanySets,
  selectedCompanyMapper = null,
  onApplySelections,
  onApplyCompanySets,
  onApplyMapper,
}: CompanySelectionModalProps) => {
  const toast = useToast();
  const { startRequest, resetRequest } = useCompanySelectionGate();
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const [activeSubModal, setActiveSubModal] = useState<SubModalView>('none');
  const [closeIntent, setCloseIntent] = useState<CloseIntent>(null);
  const [isMapCompaniesDirty, setIsMapCompaniesDirty] = useState(false);
  const [subsidiaryMappings, setSubsidiaryMappings] = useState<ColumnMappings>(
    {}
  );
  const [subsidiaryFlowSource, setSubsidiaryFlowSource] =
    useState<SubsidiaryFlowSource>('entity_selection');
  const closeDialogCancelRef = useRef<HTMLButtonElement | null>(null);
  const {
    register,
    formState: { errors, isSubmitting: isRefFormSubmitting },
    handleSubmit,
    reset,
    getValues,
  } = useForm<CompanyRefFormValues>({
    defaultValues: {
      companyReference: parseSnowflakePath(
        selectedCompanyMapper?.company_reference
      ),
      pipelineInput: parseSnowflakePath(selectedCompanyMapper?.pipeline_input),
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    reset({
      companyReference: parseSnowflakePath(
        selectedCompanyMapper?.company_reference
      ),
      pipelineInput: parseSnowflakePath(selectedCompanyMapper?.pipeline_input),
    });
  }, [
    isOpen,
    selectedCompanyMapper?.company_reference,
    selectedCompanyMapper?.pipeline_input,
    reset,
  ]);

  const closeEntireFlow = () => {
    setActiveSubModal('none');
    setIsMapCompaniesDirty(false);
    onClose();
  };

  const runCloseIntent = (intent: Exclude<CloseIntent, null>) => {
    if (intent === 'close_all') {
      closeEntireFlow();
      return;
    }

    setActiveSubModal('none');
  };

  const requestClose = (intent: Exclude<CloseIntent, null>) => {
    const isMapCompaniesCloseAttempt =
      activeSubModal === 'map_companies' && isMapCompaniesDirty;

    if (isMapCompaniesCloseAttempt) {
      setCloseIntent(intent);
      return;
    }

    runCloseIntent(intent);
  };

  const handleClose = () => {
    requestClose('close_all');
  };

  const handleSubModalClose = () => {
    requestClose('close_submodal');
  };

  const handleCardClick = (cardId: string) => {
    if (cardId === COMMON_SET_CARD_ID) {
      setActiveSubModal('common_set_selection');
      return;
    }
    if (cardId === 'choose_from_list') {
      setActiveSubModal('ref_table');
      return;
    }
    if (cardId === DEFAULT_CARD_ID) {
      setActiveSubModal('entity_selection');
      return;
    }
    if (cardId === 'map_companies') {
      setIsMapCompaniesDirty(false);
      setActiveSubModal('map_companies');
    }
  };

  const handleApplyRefTable = handleSubmit((values) => {
    resetRequest(instanceId);
    const pipelineInputPath = joinSnowflakePath(values.pipelineInput);
    onApplyMapper({
      type: 'company_mapper',
      company_reference: joinSnowflakePath(values.companyReference),
      pipeline_input: pipelineInputPath === '' ? null : pipelineInputPath,
      selection_type: 'table',
    });
    reset(values);
    closeEntireFlow();
  });

  const companySetOptions = (() => {
    if (isRevelioAdmin) {
      return SUPPORTED_COMPANY_OPTIONS;
    }
    return SUPPORTED_COMPANY_OPTIONS.filter(
      (option) => !ONLY_REVELIO_ADMIN_OPTIONS.includes(option.value)
    );
  })();

  const selectedCompanySetOption =
    selectedCompanySets === null || selectedCompanySets.length === 0
      ? null
      : (companySetOptions.find(
          (option) => option.value === selectedCompanySets[0]
        ) ?? null);

  const handleSelectCompanySet = (option: SingleValue<CompanySetOption>) => {
    if (option === null) {
      return;
    }
    resetRequest(instanceId);
    onApplyCompanySets([option.value]);
    closeEntireFlow();
  };

  const filteredCardOptions = CARD_OPTIONS.filter(
    (card) => !card.adminOnly || isRevelioAdmin
  );
  const activeCardId = (() => {
    if (selectedCompanySets !== null && selectedCompanySets.length > 0) {
      return COMMON_SET_CARD_ID;
    }

    if (selectedCompanyMapper !== null) {
      const fromSelectionType = cardIdForSelectionType(
        selectedCompanyMapper.selection_type
      );
      if (fromSelectionType !== null) {
        return fromSelectionType;
      }

      if (Object.keys(selectedCompanySelections).length === 0) {
        return CHOOSE_FROM_LIST_CARD_ID;
      }

      return DEFAULT_CARD_ID;
    }

    if (Object.keys(selectedCompanySelections).length > 0) {
      return DEFAULT_CARD_ID;
    }

    return null;
  })();

  /** Quick path: "Add Companies" — submit directly without subsidiary editing */
  const handleAddCompanies = (selections: Subsidiary[]) => {
    if (selections.length === 0) {
      resetRequest(instanceId);
      onApplySelections({});
      onApplyMapper(null);
      closeEntireFlow();
      return;
    }

    const selectionMap = toCompanySelectionMap(selections);
    onApplySelections(selectionMap);
    onApplyMapper({
      type: 'company_mapper',
      pipeline_input: '',
      company_reference: '',
      selection_type: 'selection',
    });
    closeEntireFlow();

    const { complete, fail } = startRequest(instanceId);
    createCompanyMapperFromSelections(selectionMap, 'selection').then(
      (mapper) => {
        onApplyMapper(mapper);
        complete();
      },
      () => {
        onApplyMapper(null);
        toast({
          title: 'Failed to create company reference table',
          status: 'error',
        });
        fail();
      }
    );
  };

  /** Edit path: "Edit Subsidiaries" — open MapperModal for subsidiary editing */
  const handleEditSubsidiaries = async (selections: Subsidiary[]) => {
    const formatted = selections.map(normalizeToSubsidiary);
    const enriched = await enrichSubsidiaryList(formatted);
    const initialMappings = buildInitialMappings(enriched);
    setSubsidiaryMappings(initialMappings);
    setSubsidiaryFlowSource('entity_selection');
    setActiveSubModal('subsidiary_mapping');
  };

  /** MapperModal submit: transform subsidiary hierarchy and call API */
  const handleSubsidiarySubmit = async (finalMappings: ColumnMappings) => {
    const selectionMap = mappingsToSelectionMap(finalMappings);
    const selectionType: CompanyMapperSelectionType =
      subsidiaryFlowSource === 'map_companies' ? 'file' : 'selection';
    onApplySelections(selectionMap);
    onApplyMapper({
      type: 'company_mapper',
      pipeline_input: '',
      company_reference: '',
      selection_type: selectionType,
    });
    closeEntireFlow();

    const { complete, fail } = startRequest(instanceId);
    try {
      const mapper = await createCompanyMapperFromMappings(
        finalMappings,
        selectionType
      );
      onApplyMapper(mapper);
      complete();
    } catch {
      onApplyMapper(null);
      toast({
        title: 'Failed to create company reference table',
        status: 'error',
      });
      fail();
    }
  };

  const handleConfirmClose = () => {
    if (closeIntent !== null) {
      runCloseIntent(closeIntent);
    }
    setCloseIntent(null);
  };

  const handleCancelClose = () => {
    setCloseIntent(null);
  };

  return (
    <>
      {/* Card grid modal — stays visible behind nested modals */}
      <Modal
        isOpen={isOpen && activeSubModal !== 'map_companies'}
        onClose={handleClose}
        isCentered
        size="xl"
        blockScrollOnMount={false}
      >
        <ModalOverlay />
        <ModalContent data-testid="data-builder-company-selection-modal">
          <ModalHeader color="text.primary" fontWeight="semibold">
            Select Companies
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={6}>
            <SimpleGrid columns={2} spacing={4}>
              {filteredCardOptions.map((card) => (
                <WithTooltip
                  key={card.id}
                  showCondition={!card.enabled}
                  label={FUTURE_RELEASE_TOOLTIP}
                  hasArrow
                >
                  <div>
                    <SelectionOptionCard
                      key={card.id}
                      testId={`data-builder-company-card-${card.id}`}
                      label={card.label}
                      description={card.description}
                      icon={card.icon}
                      isDisabled={!card.enabled}
                      isSelected={card.id === activeCardId}
                      onClick={() => handleCardClick(card.id)}
                    />
                  </div>
                </WithTooltip>
              ))}
            </SimpleGrid>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Entity selections modal — layered on top of a card grid */}
      <CompanySelectionsModal
        header="Select Companies"
        isOpen={isOpen && activeSubModal === 'entity_selection'}
        onClose={handleSubModalClose}
        onCancel={handleSubModalClose}
        closeOnSubmit={false}
        allowEmptySubmit
        submitHandler={handleAddCompanies}
        entityMenuHandlers={[
          {
            text: 'Edit Subsidiaries',
            handler: handleEditSubsidiaries,
          },
        ]}
        submitText="Add Companies"
        initialSelections={selectedCompanySelections}
        submitIsLoading={false}
        showPlaceholders
      />

      <Modal
        isOpen={isOpen && activeSubModal === 'common_set_selection'}
        onClose={handleSubModalClose}
        isCentered
        size="lg"
        blockScrollOnMount={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary" fontWeight="semibold">
            Choose a set
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pt={2}>
            <Select<CompanySetOption, false>
              options={companySetOptions}
              value={selectedCompanySetOption}
              onChange={handleSelectCompanySet}
            />
          </ModalBody>
          <ModalFooter>
            <HStack gap={1}>
              <Button variant="ghost" onClick={handleSubModalClose}>
                Cancel
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isOpen && activeSubModal === 'ref_table'}
        onClose={handleSubModalClose}
        isCentered
        size="lg"
        blockScrollOnMount={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary" fontWeight="semibold">
            Company Ref Table
          </ModalHeader>
          <ModalCloseButton />
          <Divider orientation="horizontal" />
          <ModalBody mt="14px">
            <Heading color="text.primary" fontWeight="600" size="sm" mb="2">
              <Flex alignItems="center" gap={1}>
                Company Ref Table
                <Tooltip
                  label='Required columns: "company_id", "company", "rcid"'
                  textAlign="center"
                  hasArrow
                  placement="top"
                >
                  <InfoOutlineIcon
                    boxSize={3}
                    color="text.primary"
                    cursor="pointer"
                  />
                </Tooltip>
              </Flex>
            </Heading>
            <SnowflakeDbInputs
              testIdPrefix="data-builder-company-reference"
              register={
                ((name, options) =>
                  register(
                    `companyReference.${name}` as const,
                    options as RegisterOptions<
                      CompanyRefFormValues,
                      `companyReference.${keyof SnowflakeDbValues}`
                    >
                  )) as SnowflakeRegister
              }
              errors={
                errors.companyReference as FormState<SnowflakeDbValues>['errors']
              }
            />

            <Heading
              color="text.primary"
              fontWeight="600"
              size="sm"
              mb="2"
              mt="8"
            >
              <Flex alignItems="center" gap={1}>
                Pipeline Input
                <Tooltip
                  label='Required columns: "company", "rcid"'
                  textAlign="center"
                  hasArrow
                  placement="top"
                >
                  <InfoOutlineIcon
                    boxSize={3}
                    color="text.primary"
                    cursor="pointer"
                  />
                </Tooltip>
              </Flex>
            </Heading>
            <SnowflakeDbInputs
              testIdPrefix="data-builder-pipeline-input"
              isOptional
              register={
                ((name, options) =>
                  register(`pipelineInput.${name}` as const, {
                    ...(options as RegisterOptions<
                      CompanyRefFormValues,
                      `pipelineInput.${keyof SnowflakeDbValues}`
                    >),
                    deps: PIPELINE_INPUT_FIELDS.filter(
                      (field) => field.name !== name
                    ).map((field) => `pipelineInput.${field.name}` as const),
                    validate: (value) => {
                      const v = getValues('pipelineInput');
                      const anyFilled = PIPELINE_INPUT_FIELDS.some(
                        (f) => (v?.[f.name] ?? '') !== ''
                      );
                      if (!anyFilled) return true;
                      if (typeof value === 'string' && value !== '') {
                        return true;
                      }
                      const label =
                        PIPELINE_INPUT_FIELDS.find((f) => f.name === name)
                          ?.label ?? name;
                      return `${label} is required`;
                    },
                  })) as SnowflakeRegister
              }
              errors={
                errors.pipelineInput as FormState<SnowflakeDbValues>['errors']
              }
            />
          </ModalBody>
          <ModalFooter>
            <HStack gap={1}>
              <Button variant="ghost" onClick={handleSubModalClose}>
                Back
              </Button>
              <Button
                color="primary"
                onClick={handleApplyRefTable}
                disabled={isRefFormSubmitting}
              >
                Set reference table
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Subsidiary mapping modal — layered on top */}
      <DataBuilderSubsidiaryMapping
        isOpen={isOpen && activeSubModal === 'subsidiary_mapping'}
        onClose={handleSubModalClose}
        onBack={() =>
          setActiveSubModal(
            subsidiaryFlowSource === 'map_companies'
              ? 'map_companies'
              : 'entity_selection'
          )
        }
        initialMappings={subsidiaryMappings}
        onSubmit={handleSubsidiarySubmit}
        submitIsLoading={false}
      />

      <DataBuilderMapCompaniesFlow
        instanceId={instanceId}
        isOpen={isOpen && activeSubModal === 'map_companies'}
        onRequestClose={handleSubModalClose}
        onApplySelections={onApplySelections}
        onApplyMapper={onApplyMapper}
        onFlowComplete={closeEntireFlow}
        onSubsidiaryEdit={(mappings) => {
          setSubsidiaryMappings(mappings);
          setSubsidiaryFlowSource('map_companies');
          setActiveSubModal('subsidiary_mapping');
        }}
        onDirtyChange={setIsMapCompaniesDirty}
      />

      <AlertDialog
        isOpen={closeIntent !== null}
        leastDestructiveRef={closeDialogCancelRef}
        onClose={handleCancelClose}
        isCentered
        blockScrollOnMount={false}
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader color="text.primary" fontWeight="semibold">
            Abandon changes?
          </AlertDialogHeader>
          <AlertDialogBody color="text.primary">
            You have unsaved map companies changes. Are you sure you want to
            abandon them?
          </AlertDialogBody>
          <AlertDialogFooter>
            <HStack gap={2}>
              <ChakraButton
                ref={closeDialogCancelRef}
                variant="outline"
                colorScheme="gray"
                color="text.primary"
                onClick={handleCancelClose}
              >
                No, Go Back
              </ChakraButton>
              <Button color="destructive" onClick={handleConfirmClose}>
                Yes, Abandon
              </Button>
            </HStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
